import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'

// Global styles
const globalStyles = `
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background: #0f172a;
    color: #ffffff;
    overflow: hidden;
    user-select: none;
  }

  #root {
    width: 100vw;
    height: 100vh;
  }

  /* Disable text selection for game elements */
  .game-screen, .letter-display, .finger-hints {
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }

  /* Focus styles */
  button:focus,
  select:focus,
  input:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }

  /* Smooth animations */
  * {
    transition-property: color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 150ms;
  }

  /* Disable context menu during training */
  .game-screen * {
    -webkit-context-menu: none;
    -moz-context-menu: none;
    -ms-context-menu: none;
    context-menu: none;
  }

  /* Custom scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: rgba(75, 85, 99, 0.3);
  }

  ::-webkit-scrollbar-thumb {
    background: rgba(156, 163, 175, 0.5);
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: rgba(156, 163, 175, 0.7);
  }

  /* Prevent zoom on mobile */
  @media (max-width: 768px) {
    input, select, textarea, button {
      font-size: 16px !important;
    }
  }

  /* High contrast mode support */
  @media (prefers-contrast: high) {
    * {
      border-color: #ffffff !important;
    }
  }

  /* Reduced motion support */
  @media (prefers-reduced-motion: reduce) {
    * {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }

  /* Print styles */
  @media print {
    body {
      background: white !important;
      color: black !important;
    }
    
    .game-screen {
      background: white !important;
    }
    
    .finger-hints,
    .progress-visualization,
    .real-time-stats {
      display: none !important;
    }
  }
`;

// Inject global styles
const styleSheet = document.createElement('style');
styleSheet.textContent = globalStyles;
document.head.appendChild(styleSheet);

// Initialize React app
const rootElement = document.getElementById('root');
if (rootElement) {
  const root = ReactDOM.createRoot(rootElement);
  
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
} else {
  console.error('Root element not found. Make sure index.html has a div with id="root"');
}

// Service Worker registration (for future PWA support)
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        console.log('SW registered: ', registration);
      })
      .catch((registrationError) => {
        console.log('SW registration failed: ', registrationError);
      });
  });
}

// Global error handler
window.addEventListener('error', (event) => {
  console.error('Global error:', event.error);
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
});

// Prevent context menu and certain keyboard shortcuts during gameplay
document.addEventListener('contextmenu', (e) => {
  if ((e.target as Element)?.closest('.game-screen')) {
    e.preventDefault();
  }
});

document.addEventListener('keydown', (e) => {
  // Prevent F5 refresh during gameplay
  if (e.key === 'F5' && (e.target as Element)?.closest('.game-screen')) {
    e.preventDefault();
  }
  
  // Prevent Ctrl+R refresh during gameplay
  if ((e.ctrlKey || e.metaKey) && e.key === 'r' && (e.target as Element)?.closest('.game-screen')) {
    e.preventDefault();
  }
  
  // Prevent browser zoom during gameplay
  if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '-' || e.key === '0') && (e.target as Element)?.closest('.game-screen')) {
    e.preventDefault();
  }
});

// Performance monitoring
if (import.meta.env.DEV) {
  // Monitor performance in development
  let startTime = performance.now();
  
  window.addEventListener('load', () => {
    const loadTime = performance.now() - startTime;
    console.log(`App loaded in ${loadTime.toFixed(2)}ms`);
  });
  
  // Log render performance
  const originalConsoleLog = console.log;
  console.log = (...args) => {
    if (args[0]?.includes?.('render')) {
      const timestamp = new Date().toLocaleTimeString();
      originalConsoleLog(`[${timestamp}]`, ...args);
    } else {
      originalConsoleLog(...args);
    }
  };
}

// Development helpers
if (import.meta.env.DEV) {
  // Global debugging helpers
  (window as any).KeyboardHeroDebug = {
    version: '1.0.0',
    build: 'development',
    clearStorage: () => {
      localStorage.clear();
      sessionStorage.clear();
      console.log('Storage cleared');
    },
    getStorageUsage: () => {
      const storage = { ...localStorage };
      const usage = JSON.stringify(storage).length;
      console.log(`Storage usage: ${usage} bytes`);
      return usage;
    }
  };
  
  // 🛠️ Load data collection debug functions
  import('./debug/dataCollectionTest').then(() => {
    console.log('🧪 Data collection debug functions loaded');
    console.log('📝 Run __testStatisticsCalculator() to test statistics');
    console.log('🔍 Run __verifyFirebaseRound("roundId") to verify Firebase data');
  }).catch(err => {
    console.warn('⚠️ Could not load debug functions:', err);
  });
  
  console.log('KeyboardHero Debug Tools available:', (window as any).KeyboardHeroDebug);
}
